import React from "react";
import { X } from "lucide-react";

export const cx = (...c) => c.filter(Boolean).join(" ");

export const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_5247ce2b-f829-432a-bf58-0b4960efbcfb/artifacts/qeyz03eh_SpinaGO.png";

export function Button({ children, variant = "primary", size = "md", className, ...p }) {
  const variants = {
    primary: "bg-teal text-white hover:bg-teal-600 shadow-soft",
    navy: "bg-navy text-white hover:bg-navy-700",
    outline: "border border-slate-300 text-navy hover:bg-vlg",
    ghost: "text-navy hover:bg-slate-100",
    danger: "bg-alert text-white hover:opacity-90",
    warn: "bg-warn text-white hover:opacity-90",
  };
  const sizes = { sm: "px-3 py-1.5 text-sm", md: "px-4 py-2.5 text-sm", lg: "px-6 py-3 text-base" };
  return (
    <button
      className={cx("inline-flex items-center justify-center gap-2 rounded-lg font-medium font-head transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal/40 disabled:opacity-50 disabled:cursor-not-allowed active:translate-y-px",
        variants[variant], sizes[size], className)}
      {...p}
    >
      {children}
    </button>
  );
}

export function Card({ children, className, ...p }) {
  return <div className={cx("bg-white border border-slate-200 rounded-xl2 shadow-soft", className)} {...p}>{children}</div>;
}

export function Label({ children, className }) {
  return <label className={cx("block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5", className)}>{children}</label>;
}

export function Input({ className, ...p }) {
  return <input className={cx("w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-teal focus:ring-2 focus:ring-teal/20 focus:outline-none transition", className)} {...p} />;
}

export function Textarea({ className, ...p }) {
  return <textarea className={cx("w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-teal focus:ring-2 focus:ring-teal/20 focus:outline-none transition", className)} {...p} />;
}

export function Select({ className, children, ...p }) {
  return <select className={cx("w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 focus:border-teal focus:ring-2 focus:ring-teal/20 focus:outline-none transition", className)} {...p}>{children}</select>;
}

export function Field({ label, children }) {
  return <div><Label>{label}</Label>{children}</div>;
}

export function Badge({ children, tone = "default", className }) {
  const tones = {
    default: "bg-slate-100 text-slate-600",
    teal: "bg-teal-50 text-teal-600",
    navy: "bg-skyx text-navy",
    warn: "bg-orange-100 text-warn",
    alert: "bg-red-100 text-alert",
    green: "bg-emerald-100 text-emerald-700",
  };
  return <span className={cx("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold", tones[tone], className)}>{children}</span>;
}

export function Spinner({ className }) {
  return <div className={cx("animate-spin rounded-full border-2 border-slate-200 border-t-teal h-6 w-6", className)} />;
}

export function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy/40 backdrop-blur-sm p-4 py-10" onClick={onClose}>
      <div className={cx("w-full bg-white rounded-xl2 shadow-card animate-fade", sizes[size])} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-semibold font-head text-navy">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-navy p-1"><X size={20} /></button>
        </div>
        <div className="p-6 spina-scroll max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "teal" }) {
  const tones = { teal: "text-teal bg-teal-50", navy: "text-navy bg-skyx", warn: "text-warn bg-orange-50", alert: "text-alert bg-red-50" };
  return (
    <Card className="p-5 flex items-center gap-4 animate-fade">
      {Icon && <div className={cx("h-11 w-11 rounded-lg flex items-center justify-center", tones[tone])}><Icon size={22} /></div>}
      <div>
        <div className="text-2xl font-bold font-head text-navy leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </Card>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="text-center py-16 text-slate-400">
      <p className="font-medium text-slate-500">{title}</p>
      {hint && <p className="text-sm mt-1">{hint}</p>}
    </div>
  );
}

export function Slider({ value, onChange, max = 10, tone = "teal", ...p }) {
  return (
    <div className="flex items-center gap-3">
      <input type="range" min="0" max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} aria-label="niveau"
        className={cx("flex-1 h-3 cursor-pointer", tone === "alert" ? "range-alert" : "range-teal")} {...p} />
      <span className={cx("w-8 text-center font-bold font-head", tone === "alert" ? "text-alert" : "text-teal")}>{value}</span>
    </div>
  );
}
